import axios from "axios";
import Cookies from "js-cookie";
import { getActiveOfficeCodeFromStorage } from "../../../../utils/officeCodeContext";
import {
  extractStrategyMetadataItemsFromResponse,
  normalizeStrategyMetadataRow,
} from "../parityConfigMappers";

const API_ROOT =
  process.env.REACT_APP_RESOURCE_SELECTION_API_BASE_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  "http://localhost:8080";

/** Location endpoints live on the main API host (not a separate RS base URL). */
const LOCATIONS_API_ROOT = process.env.REACT_APP_API_BASE_URL || API_ROOT;

const getAuthToken = () =>
  Cookies.get("authToken") || localStorage.getItem("authToken") || "";

const withAuthHeaders = () => {
  const token = getAuthToken();
  const officeCode = getActiveOfficeCodeFromStorage();

  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(officeCode ? { "Office-Code": officeCode } : {}),
  };
};

const locationApiHeaders = () => {
  const token = getAuthToken();
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    Accept: "application/json",
  };
};

const fetchLocationsByOfficeCodesRequest = (officeCodes = []) => {
  const codes = (Array.isArray(officeCodes) ? officeCodes : [])
    .map((code) => String(code || "").trim())
    .filter(Boolean);
  if (!codes.length) {
    return Promise.resolve({ data: [] });
  }
  return axios.get(`${LOCATIONS_API_ROOT}/api/locations/by-office-codes`, {
    params: {
      officeCodes: codes,
      sortBy: "locationName",
      ascending: true,
    },
    paramsSerializer: { indexes: null },
    headers: locationApiHeaders(),
  });
};


const parityScopeHeaders = ({ originatorId, originatorCode, userId } = {}) => ({
  ...withAuthHeaders(),
  ...(originatorId ? { "X-Originator-Id": originatorId } : {}),
  ...(originatorCode ? { "X-Originator-Code": originatorCode } : {}),
  ...(userId ? { "X-User-Id": userId } : {}),
});

/** POST /api/resource-selection/shipments/{id}/select — parity run (config match + optional rate shopping). */
const postResourceSelectionForShipment = (shipmentId, body = {}, extraHeaders = {}) =>
  axios.post(
    `${API_ROOT}/api/resource-selection/shipments/${encodeURIComponent(String(shipmentId))}/select`,
    body,
    {
      headers: {
        "Content-Type": "application/json",
        ...withAuthHeaders(),
        ...extraHeaders,
      },
    }
  );

export const resourceSelectionApi = {
  // --- Parity: carrier selection configurations (primary UI) ---

  listCarrierSelectionConfigs: ({
    originatorId,
    originatorCode,
    officeCodes,
    page = 1,
    pageSize = 100,
  }) => {
    const token = Cookies.get("authToken");
    const cookieOffice = Cookies.get("officeCode");
    const officeHeader = Array.isArray(officeCodes) && officeCodes.length
      ? officeCodes.join(",")
      : cookieOffice;

    return axios.get(`${API_ROOT}/api/resource-selection/carrier-selection-configs`, {
      params: {
        ...(originatorId ? { originator_id: originatorId } : {}),
        ...(originatorCode ? { originator_code: originatorCode } : {}),
        page,
        page_size: pageSize,
        size: pageSize,
      },
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(officeHeader ? { "Office-Code": officeHeader } : {}),
      },
    });
  },

  getCarrierSelectionConfig: (configUuid, { originatorId, originatorCode } = {}) =>
    axios.get(
      `${API_ROOT}/api/resource-selection/carrier-selection-configs/${encodeURIComponent(
        String(configUuid)
      )}`,
      {
        headers: parityScopeHeaders({ originatorId, originatorCode }),
      }
    ),

  createCarrierSelectionConfig: (body, { userId } = {}) =>
    axios.post(`${API_ROOT}/api/resource-selection/carrier-selection-configs`, body, {
      headers: {
        "Content-Type": "application/json",
        ...parityScopeHeaders({ userId }),
      },
    }),

  updateCarrierSelectionConfig: (configUuid, body, { originatorId, originatorCode, userId } = {}) =>
    axios.put(
      `${API_ROOT}/api/resource-selection/carrier-selection-configs/${encodeURIComponent(
        String(configUuid)
      )}`,
      body,
      {
        headers: {
          "Content-Type": "application/json",
          ...parityScopeHeaders({ originatorId, originatorCode, userId }),
        },
      }
    ),

  deleteCarrierSelectionConfig: (configUuid, { originatorId, originatorCode } = {}) =>
    axios.delete(
      `${API_ROOT}/api/resource-selection/carrier-selection-configs/${encodeURIComponent(
        String(configUuid)
      )}`,
      {
        headers: parityScopeHeaders({ originatorId, originatorCode }),
      }
    ),

  patchCarrierConfigActive: (configUuid, body, { originatorId, originatorCode, userId } = {}) =>
    axios.patch(
      `${API_ROOT}/api/resource-selection/carrier-selection-configs/${encodeURIComponent(
        String(configUuid)
      )}/set-active`,
      body,
      {
        headers: {
          "Content-Type": "application/json",
          ...parityScopeHeaders({ originatorId, originatorCode, userId }),
        },
      }
    ),

  listStrategyTypeMetadata: (page = 0, pageSize = 100, scope = {}) =>
    axios.get(`${API_ROOT}/api/resource-selection/config-type-metadata`, {
      params: { page, page_size: pageSize, size: pageSize },
      headers: parityScopeHeaders(scope),
    }),

  /** Fallback when paged params return an empty or non-standard body. */
  listStrategyTypeMetadataUnpaged: (scope = {}) =>
    axios.get(`${API_ROOT}/api/resource-selection/config-type-metadata`, {
      headers: parityScopeHeaders(scope),
    }),

  getStrategyTypeMetadata: (strategyMetadataId) =>
    axios.get(
      `${API_ROOT}/api/resource-selection/config-type-metadata/${encodeURIComponent(
        String(strategyMetadataId)
      )}`,
      { headers: withAuthHeaders() }
    ),

  getResourceSelectionEnums: (scope = {}) =>
    axios.get(`${API_ROOT}/api/resource-selection/enums`, {
      headers: parityScopeHeaders(scope),
    }),

  searchLocations: (payload) =>
    axios.post(`${LOCATIONS_API_ROOT}/api/locations/search`, payload, {
      headers: {
        "Content-Type": "application/json",
        ...locationApiHeaders(),
        ...withAuthHeaders(),
      },
    }),

  listLocationsGrouped: () =>
    axios.get(`${LOCATIONS_API_ROOT}/api/locations/grouped`, {
      headers: locationApiHeaders(),
    }),

  listLocationsByOfficeCode: (officeCode) =>
    fetchLocationsByOfficeCodesRequest(officeCode ? [String(officeCode).trim()] : []),

  listLocationsByOfficeCodes: (officeCodes = []) =>
    fetchLocationsByOfficeCodesRequest(officeCodes),


  runSelection: (payload) =>
    axios.post(`${API_ROOT}/api/resource-selection/select`, payload, {
      headers: {
        "Content-Type": "application/json",
        ...withAuthHeaders(),
      },
    }),

  /** Lane path discovery + carrier ranking for simulation (synthetic shipment, no shipmentNumber). */
  fetchLanePathsForSimulation: ({
    origin,
    destination,
    officeCode,
    operationDate,
    paymentMethod = "PREPAID",
    originType = "GEOGRAPHY",
    destinationType = "GEOGRAPHY",
    originAddress,
    destinationAddress,
    totalMiles,
    carrierCode,
    configId,
    transportMode,
    shipmentType,
    scoringSpec,
    scoringSpecJson,
  }) => {
    const requestBody = {
      ...(scoringSpec ? { scoring_spec: scoringSpec, scoringSpec } : {}),
      ...(configId
        ? {
            carrier_selection_config_id: configId,
            carrierSelectionConfigId: configId,
          }
        : {}),
    };

    return axios.post(
      `${API_ROOT}/api/organizations/${encodeURIComponent(String(officeCode))}/rate-shopping/lane-paths`,
      requestBody,
      {
        params: {
          origin,
          destination,
          originAddress: originAddress || undefined,
          destinationAddress: destinationAddress || undefined,
          originType,
          destinationType,
          operationDate,
          paymentMethod,
          totalMiles: totalMiles ?? undefined,
          carrierCode: carrierCode || undefined,
          ...(configId
            ? {
                carrier_selection_config_id: configId,
                carrierSelectionConfigId: configId,
              }
            : {}),
          ...(transportMode
            ? { transport_mode: transportMode, transportMode }
            : {}),
          ...(shipmentType ? { shipment_type: shipmentType, shipmentType } : {}),
          ...(scoringSpecJson
            ? { scoring_spec_json: scoringSpecJson, scoringSpecJson }
            : {}),
        },
        headers: {
          "Content-Type": "application/json",
          ...withAuthHeaders(),
          ...(officeCode ? { "Office-Code": officeCode } : {}),
        },
      }
    );
  },

  /** Organization directory for resolving carrier office codes to display names. */
  listOrganizationCarriers: () =>
    axios.get(`${API_ROOT}/api/organizations/get-carriers`, {
      headers: withAuthHeaders(),
    }),


  /**
   * Run resource selection + rate shopping for a shipment (parity).
   * Body: ship_date, lane_limit, skip_rate_shopping, skip_shipment_tender (snake_case).
   */
  selectForShipment: postResourceSelectionForShipment,

  selectShipmentById: postResourceSelectionForShipment,

  listShipmentSelectionOutcomes: (shipmentId, scope = {}) =>
    axios.get(
      `${API_ROOT}/api/resource-selection/shipments/${encodeURIComponent(
        String(shipmentId)
      )}/selection-outcomes`,
      { headers: parityScopeHeaders(scope) }
    ),

  getScoringExplanation: (shipmentId, outcomeId, { carrierCode, ...scope } = {}) =>
    axios.get(
      `${API_ROOT}/api/resource-selection/shipments/${encodeURIComponent(
        String(shipmentId)
      )}/selection-outcomes/${encodeURIComponent(String(outcomeId))}/scoring-explanation`,
      {
        params: {
          ...(carrierCode ? { carrier_code: carrierCode, carrierCode } : {}),
        },
        headers: parityScopeHeaders(scope),
      }
    ),

  saveRankedCarrierSnapshot: (shipmentId, outcomeId, body, scope = {}) =>
    axios.post(
      `${API_ROOT}/api/resource-selection/shipments/${encodeURIComponent(
        String(shipmentId)
      )}/selection-outcomes/${encodeURIComponent(String(outcomeId))}/ranked-carriers`,
      body,
      {
        headers: {
          "Content-Type": "application/json",
          ...parityScopeHeaders(scope),
        },
      }
    ),

  // --- Legacy: configs + strategies (Long ids) ---

  listConfigsByOriginator: (originatorId) =>
    axios.get(
      `${API_ROOT}/api/resource-selection/configs/originator/${encodeURIComponent(
        String(originatorId)
      )}`,
      { headers: withAuthHeaders() }
    ),

  getLegacyConfig: (configId) =>
    axios.get(
      `${API_ROOT}/api/resource-selection/configs/${encodeURIComponent(
        String(configId)
      )}`,
      { headers: withAuthHeaders() }
    ),

  createLegacyConfig: (body) =>
    axios.post(`${API_ROOT}/api/resource-selection/configs`, body, {
      headers: {
        "Content-Type": "application/json",
        ...withAuthHeaders(),
      },
    }),

  updateLegacyConfig: (configId, body) =>
    axios.put(
      `${API_ROOT}/api/resource-selection/configs/${encodeURIComponent(
        String(configId)
      )}`,
      body,
      {
        headers: {
          "Content-Type": "application/json",
          ...withAuthHeaders(),
        },
      }
    ),

  listLegacyStrategiesByConfig: (configId) =>
    axios.get(
      `${API_ROOT}/api/resource-selection/configs/${encodeURIComponent(
        String(configId)
      )}/strategies`,
      { headers: withAuthHeaders() }
    ),

  createLegacyStrategy: (configId, body) =>
    axios.post(
      `${API_ROOT}/api/resource-selection/configs/${encodeURIComponent(
        String(configId)
      )}/strategies`,
      body,
      {
        headers: {
          "Content-Type": "application/json",
          ...withAuthHeaders(),
        },
      }
    ),

  updateLegacyStrategy: (strategyId, body) =>
    axios.put(
      `${API_ROOT}/api/resource-selection/strategies/${encodeURIComponent(
        String(strategyId)
      )}`,
      body,
      {
        headers: {
          "Content-Type": "application/json",
          ...withAuthHeaders(),
        },
      }
    ),
};

/**
 * Loads strategy / config-type metadata with parity scope headers and several URL/param fallbacks.
 * @param {string} originatorScope office or originator code (same as list configs scope)
 */
export async function fetchStrategyTypeMetadataCatalogue(originatorScope = "") {
  const code = (originatorScope || "").trim();
  const scope = code ? { originatorCode: code, originatorId: code } : {};
  const baseQuery = code ? { originator_code: code, originator_id: code } : {};
  const paths = [
    "/api/resource-selection/config-type-metadata",
    "/api/resource-selection/strategy-type-metadata",
  ];
  const paramVariants = [
    { page: 0, page_size: 500, size: 500 },
    { page: 1, page_size: 500, size: 500 },
    {},
  ];

  /** Any HTTP response received (even with empty parsed rows). */
  let sawSuccessfulCall = false;
  /** Last axios/rejection when no successful empty/full catalogue path ran. */
  let lastFailureError = null;

  for (const path of paths) {
    for (const params of paramVariants) {
      try {
        const res = await axios.get(`${API_ROOT}${path}`, {
          params: { ...baseQuery, ...params },
          headers: parityScopeHeaders(scope),
        });
        sawSuccessfulCall = true;
        const rows = extractStrategyMetadataItemsFromResponse(res.data).map(normalizeStrategyMetadataRow);
        if (rows.length) return rows;
      } catch (e) {
        lastFailureError = e;
      }
    }
  }

  try {
    const res = await axios.get(`${API_ROOT}/api/resource-selection/enums`, {
      params: { ...baseQuery },
      headers: parityScopeHeaders(scope),
    });
    sawSuccessfulCall = true;
    const d = res.data;
    if (d && typeof d === "object") {
      const namedKeys = [
        "strategy_type_metadata",
        "strategyTypeMetadata",
        "config_type_metadata",
        "configTypeMetadata",
        "strategy_metadata",
        "strategyMetadata",
      ];
      for (const k of namedKeys) {
        const arr = d[k];
        if (Array.isArray(arr) && arr.length) {
          return arr.map(normalizeStrategyMetadataRow);
        }
      }
    }
    const fromWalk = extractStrategyMetadataItemsFromResponse(d).map(normalizeStrategyMetadataRow);
    if (fromWalk.length) return fromWalk;
  } catch (e) {
    lastFailureError = e;
  }

  if (!sawSuccessfulCall && lastFailureError) {
    throw lastFailureError;
  }

  return [];
}
